import { useLocation } from "react-router-dom";
import { Helmet } from "@/components/Helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CAMPAIGN_SIGNUP_HREF,
  CAMPAIGN_SUPPORT_HREF,
  CampaignLayout,
} from "@/components/campaign/CampaignLayout";
import {
  CtaSection,
  ExternalSupportLink,
  JumpLinksStrip,
  SectionShell,
  StepList,
} from "@/components/campaign/CampaignSections";
import { captureCampaignEvent, useCampaignPageView } from "@/lib/campaignAnalytics";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Globe,
  Link2,
  Lock,
  Puzzle,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { Link } from "react-router-dom";
import { SHADOWSCAN_WORDPRESS_PLUGIN } from "@/lib/wordpressLifecycle";

const CANONICAL_URL = "https://shadowscan.com.au/help/add-domains";
const LANDING_URL = "/linkedin";
const PLUGIN_DIRECTORY_URL = SHADOWSCAN_WORDPRESS_PLUGIN.wordpressOrgUrl;
const PORTAL_URL = "/portal";
const PRIMARY_CTA_LABEL = "Get started with ShadowScan";

const TrialOnboardingHelp = () => {
  const location = useLocation();

  useCampaignPageView("linkedin_help_viewed", {
    page: "linkedin_help",
    path: location.pathname,
  });

  const handleCtaClick = (ctaLocation: string, target: string, isPrimary = false) => {
    captureCampaignEvent("linkedin_cta_clicked", {
      page: "linkedin_help",
      location: ctaLocation,
      target,
    });

    if (isPrimary) {
      captureCampaignEvent("linkedin_evaluation_started", {
        page: "linkedin_help",
        location: ctaLocation,
        target,
      });
    }
  };

  const handleSupportClick = (locationLabel: string) => {
    captureCampaignEvent("linkedin_support_clicked", {
      page: "linkedin_help",
      location: locationLabel,
      target: "support_email",
    });
  };

  return (
    <CampaignLayout
      helpHref={location.pathname}
      currentPath="help"
      headerCtaLabel="Get started"
      headerCtaHref={CAMPAIGN_SIGNUP_HREF}
    >
      <Helmet>
        <title>ShadowScan Onboarding Help | Add Domains</title>
        <meta
          name="description"
          content="Help for adding domains to ShadowScan. Follow the setup steps, resolve common issues, and contact support if onboarding stalls."
        />
        <meta property="og:title" content="ShadowScan Onboarding Help" />
        <meta
          property="og:description"
          content="Add domains, install the connector, pair the site, and resolve common WordPress onboarding issues as you get started with ShadowScan."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:image" content="https://shadowscan.com.au/og-image.png" />
        <meta
          name="twitter:title"
          content="ShadowScan Onboarding Help"
        />
        <meta
          name="twitter:description"
          content="A clear help page for adding WordPress domains to ShadowScan and working through common setup issues."
        />
      </Helmet>

      <section className="py-16 md:py-20">
        <div className="container px-4">
          <div className="max-w-4xl space-y-6">
            <div className="inline-flex rounded-full border border-primary/20 bg-primary/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] text-primary">
              onboarding help
            </div>
            <h1 className="text-4xl font-bold leading-tight md:text-5xl">
              Add domains without losing time to setup guesswork
            </h1>
            <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">
              When onboarding slows down, the time loss usually comes from working out
              whether the blocker is in the portal, the connector pairing step, or the
              billing state. Use this guide to move through setup quickly, unblock the most
              common issues, and keep your first rollout moving. If you manage many sites
              and want custom pricing for a larger account, request it during signup before
              you start adding domains.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
                <Link
                  to={PORTAL_URL}
                  onClick={() => handleCtaClick("help_hero", PORTAL_URL)}
                >
                  Continue onboarding
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <a
                  href={PLUGIN_DIRECTORY_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => handleCtaClick("help_hero", PLUGIN_DIRECTORY_URL)}
                >
                  Open plugin directory
                </a>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Need an account first?{" "}
              <Link
                to={CAMPAIGN_SIGNUP_HREF}
                onClick={() => handleCtaClick("help_hero_text", CAMPAIGN_SIGNUP_HREF, true)}
                className="text-primary underline underline-offset-4"
              >
                {PRIMARY_CTA_LABEL}
              </Link>
              .
            </p>
            <p className="max-w-3xl text-sm text-muted-foreground">
              Create your account first. If custom pricing may be a better fit, you can
              request it during signup and keep adding domains at the standard rate in the
              meantime. Billing starts when domains go live unless we have already
              confirmed different pricing for your account.
            </p>
          </div>
        </div>
      </section>

      <SectionShell
        eyebrow="Quick triage"
        title="Jump to the part of setup that is blocking you"
        description="Use the link that matches where onboarding has stalled."
        className="pt-6 md:pt-10"
      >
        <JumpLinksStrip
          title="Common stuck states"
          items={[
            { href: "#portal-steps", label: "Stuck in portal" },
            { href: "#plugin-install", label: "Stuck installing plugin" },
            { href: "#pairing-steps", label: "Stuck pairing code" },
            { href: "#billing-state", label: "Stuck at billing prompt" },
          ]}
        />
      </SectionShell>

      <SectionShell
        eyebrow="Before you start"
        title="Here is what you will need before you start"
        description="A short checklist prevents most avoidable setup issues and makes the billing steps clearer before activation."
      >
        <div className="grid gap-5 md:grid-cols-2">
          {[
            {
              icon: Globe,
              title: "Correct domain",
              description:
                "Use the live site domain you want ShadowScan to monitor. Double-check spelling and make sure it matches the WordPress site you plan to connect.",
            },
            {
              icon: Lock,
              title: "Portal access",
              description:
                "Log in to the ShadowScan portal first so you can add the domain and generate the pairing code when you reach the connector step.",
            },
            {
              icon: Puzzle,
              title: "WordPress admin access",
              description:
                `You need enough WordPress access to install and activate ${SHADOWSCAN_WORDPRESS_PLUGIN.name}, or to ask the site owner to do that step.`,
            },
            {
              icon: Wrench,
              title: "Environment notes",
              description:
                "If the site uses a custom wp-admin path, plugin restrictions, or outbound API restrictions, keep that information handy before you start.",
            },
            {
              icon: ShieldCheck,
              title: "Billing expectation",
              description:
                "Account creation is self-serve. Billing starts when domains go live unless we have already confirmed different pricing for your account.",
            },
            {
              icon: CheckCircle2,
              title: "Portfolio pricing note",
              description:
                "If you want custom pricing for a larger account, request it during signup first. You can keep using the standard rate in the meantime.",
            },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.title} className="border-border/80 bg-card/80">
                <CardHeader className="space-y-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-primary/25 bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </SectionShell>

      <SectionShell
        id="portal-steps"
        eyebrow="Portal steps"
        title="Steps to add a domain in the portal"
        description="Follow these steps in order for the smoothest setup."
      >
        <StepList
          items={[
            {
              step: "Step 1",
              title: "Create or log in to your portal account",
              description:
                "Start in the existing portal signup flow, then open the add-domain path from your account and continue setup.",
            },
            {
              step: "Step 2",
              title: "Add the domain and confirm platform details",
              description:
                "Enter the WordPress domain you want to monitor first and confirm the platform details before continuing.",
            },
            {
              step: "Step 3",
              title: `Install ${SHADOWSCAN_WORDPRESS_PLUGIN.name}`,
              description:
                `In WordPress, search for ${SHADOWSCAN_WORDPRESS_PLUGIN.slug} in Plugins → Add New or download it from the WordPress.org directory, then activate it.`,
            },
          ]}
        />
        <div id="plugin-install" className="mt-5 space-y-3">
          <a
            href={PLUGIN_DIRECTORY_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
          >
            Open the WordPress.org plugin directory
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <p className="text-sm text-muted-foreground">
            If plugin installation is restricted by host policy, user permissions, or site
            controls, ask the site owner, hosting team, or WordPress admin to complete that
            step before continuing.
          </p>
        </div>
      </SectionShell>

      <SectionShell
        id="pairing-steps"
        eyebrow="After submission"
        title="What happens next"
        description="These are the next steps after the domain has been entered in the portal."
      >
        <div className="grid gap-5 lg:grid-cols-3">
          {[
            {
              icon: Link2,
              title: "Generate a short-lived pairing code",
              description:
                "Inside the portal onboarding dialog, verify first, then generate a pairing code. The code expires after a short window, so use it promptly.",
            },
            {
              icon: ShieldCheck,
              title: "Complete pairing in WordPress",
              description:
                `Enter the pairing code inside ${SHADOWSCAN_WORDPRESS_PLUGIN.name}. If the site uses a custom wp-admin path, save that path so the portal opens the correct admin page.`,
            },
            {
              icon: CheckCircle2,
              title: "Finish activation and monitor state",
              description:
                "If billing appears during activation, check the portal status first. Monitoring may not continue until that step is complete on the standard path. If you have requested custom pricing, you can keep moving on the standard rate until we confirm anything different.",
            },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.title} className="border-border/80 bg-card/80">
                <CardHeader className="space-y-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-primary/25 bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </SectionShell>

      <SectionShell
        eyebrow="Common issues"
        title="The blockers that cause most onboarding stalls"
        description="Use this list to narrow the problem before you contact support."
      >
        <div className="grid gap-5 md:grid-cols-2">
          {[
            {
              title: "Incorrect domain entry",
              description:
                "If the wrong hostname or URL was entered in the portal, the pairing and connector steps may not line up with the site you are trying to protect.",
            },
            {
              title: "Domain or site mismatch",
              description:
                "Make sure the WordPress site you are installing the connector on is the same site represented by the portal entry.",
            },
            {
              title: "Custom wp-admin URL not saved",
              description:
                "If the site does not use /wp-admin/, save the correct admin URL in the portal onboarding dialog before relying on the generated pairing link.",
            },
            {
              title: "Expired pairing code",
              description:
                "Pairing codes are short-lived. Generate a fresh code if the original one times out before it is entered in the plugin.",
            },
            {
              title: "Plugin or host restrictions",
              description:
                "Some WordPress hosts or site policies restrict plugin installation, activation, or outbound requests. Confirm those controls early if setup stalls unexpectedly.",
            },
            {
              title: "Outbound or API restrictions",
              description:
                "If the site cannot complete outbound requests, connector pairing may fail even if the plugin installs correctly.",
            },
            {
              title: "Incomplete billing or setup state",
              description:
                "If the connector looks paired but monitoring is not moving forward, check the portal status first. Activation may pause until the billing step is completed on the standard path.",
            },
          ].map((item) => (
            <Card
              key={item.title}
              id={item.title === "Incomplete billing or setup state" ? "billing-state" : undefined}
              className="border-border/80 bg-card/80"
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-lg">
                  <AlertTriangle className="h-5 w-5 text-accent" />
                  {item.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </SectionShell>

      <SectionShell
        eyebrow="If setup still fails"
        title="Escalate with enough context to get useful help quickly"
        description="Keep the email clear and specific so support can narrow the problem without a long back-and-forth."
      >
        <Card className="border-border/80 bg-card/80">
          <CardContent className="grid gap-5 p-6 lg:grid-cols-[1fr,0.85fr]">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Include these details in your message</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                {[
                  "The domain you were adding",
                  "Whether the issue happened in the portal, plugin install, pairing, or billing step",
                  "Any custom wp-admin path or host/plugin restrictions",
                  "What you expected to happen next",
                  "The exact error message, or a screenshot if that is faster",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[#22C373]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4 rounded-2xl border border-border/70 bg-background/60 p-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-primary">
                Contact support
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Use support when the onboarding guide does not unblock the issue or when the
                site environment is clearly preventing the connector path from completing.
              </p>
              <ExternalSupportLink
                href={CAMPAIGN_SUPPORT_HREF}
                label="Email support@shadowscan.com.au"
                onClick={() => handleSupportClick("help_support_section")}
              />
            </div>
          </CardContent>
        </Card>
      </SectionShell>

      <SectionShell
        eyebrow="Next step"
        title="Return to the landing page or go straight into the portal"
      >
        <CtaSection
          eyebrow="Get started"
          title="Get started with ShadowScan, then use this guide if onboarding slows down"
          description="This keeps the path direct while still giving teams a clear support page if setup stalls as they are getting started."
          primaryLabel={PRIMARY_CTA_LABEL}
          primaryHref={CAMPAIGN_SIGNUP_HREF}
          secondaryLabel="Back to campaign landing page"
          secondaryHref={LANDING_URL}
          onPrimaryClick={() => handleCtaClick("help_final_cta", CAMPAIGN_SIGNUP_HREF, true)}
          onSecondaryClick={() => handleCtaClick("help_final_cta", LANDING_URL)}
        />
      </SectionShell>
    </CampaignLayout>
  );
};

export default TrialOnboardingHelp;
